import { Color, Vector3 } from 'three';

import fbm from '@/libs/lygia/generative/fbm.glsl';
import noised from '@/libs/lygia/generative/noised.glsl';
import voronoi from '@/libs/lygia/generative/voronoi.glsl';

const voronoiFnc = /* glsl */ `

${noised}

vec2 hash2( vec2 p )
{
    // texture based white noise
    // return textureLod( iChannel0, (p+0.5)/256.0, 0.0 ).xy;
    
    // procedural white noise	
    return fract(sin(vec2(dot(p,vec2(127.1,311.7)),dot(p,vec2(269.5,183.3))))*43758.5453);
}
vec4 voronoiCrack( in vec2 x )
{
    vec2 ip = floor(x);
    vec2 fp = fract(x);

    vec2 mg, mr;
    float uniqueId = 0.0;
    vec2 cellCenter;

    float md = 8.0;
    for( int j=-1; j<=1; j++ )
    for( int i=-1; i<=1; i++ )
    {
        vec2 g = vec2(float(i),float(j));
        vec2 o = hash2( ip + g );
       
        vec2 r = g + o - fp;
        float d = dot(r,r);

        if( d<md )
        {
            md = d;
            mr = r;
            mg = g;
            uniqueId = hash2(ip + mg).x;
            cellCenter = ip + g + o; // Store the cell center
        }
    }

    md = 8.0;
    for( int j=-2; j<=2; j++ )
    for( int i=-2; i<=2; i++ )
    {
        vec2 g = mg + vec2(float(i),float(j));
        vec2 o = hash2( ip + g );
       
        vec2 r = g + o - fp;

        if( dot(mr-r,mr-r)>0.00001 )
        md = min( md, dot( 0.5*(mr+r), normalize(r-mr) ) );
    }

    return vec4( md, uniqueId, cellCenter.x, cellCenter.y ); // Return distance, uniqueId, and cell center
}
`;

const sharedMain = /* glsl */ `
    vec3 bb_corner = vec3(0.38145, 0.68065, 0.09208);
    float progressRip = max(0.0, progress - 0.25) / 0.75;
    float crackProgress = min(1.0, progress / 0.25);

    // float progress = uProgress;
    float padding = 1.0;
    float progressMapped = (progressRip) * (1.0 + padding * 2.0) - padding- 0.5;

    float stagger = 0.25;
    float transitionValue = 1.0 - getTransitionValue(position * vec3(1.0, stagger, 1.0), progressMapped);

    vec3 noise = noised(position * 2.0).xyz;
    vec3 noise2 = noised(position * 1.0).xyz;
    float noise3 = fbm(position * 3.0);
    vec4 c = voronoiCrack(position.xy * 1.0 + noise.xy * .1 + noise2.xy * .1 + noise3 * .1);
    // c *= 1.0 + fbm(position*3.0);
    vec3 center = vec3(c.zw, 0.0);

    float start = pow(transitionValue, 3.0);
    float border = 1.0-smoothstep( start - 0.01, start, c.x ) ;

`;

class CrackTransition {
    static name = 'crack';
    static uniforms = /* glsl */ `
    varying vec3 vPositionCrack;
    `;
    static vertexShader = /* glsl */ `
    ${voronoiFnc}
    vec3 transition_crack_vertex(vec3 position, vec3 normal, float progress, float time) {
        ${sharedMain}
       
        vPositionCrack = position;

        vec3 displaceNoise = noised(position * 2.0 + vec3(0.0, 0.0, u_time*.1)).xyz * .1;
         
        vec3 moveDirection = vec3(sign(center.x), 0.0, 0.0); 


        // rotate around center
        // rotate around center
        float angle = max(0.0, start) * 0.1 * -center.x * 2.0;
        mat3 rotX = mat3(
            1.0, 0.0, 0.0,
            0.0, cos(angle), -sin(angle),
            0.0, sin(angle), cos(angle)
        );
        mat3 rotY = mat3(
            cos(angle), 0.0, sin(angle),
            0.0, 1.0, 0.0,
            -sin(angle), 0.0, cos(angle)
        );
        mat3 rotZ = mat3(
            cos(angle), -sin(angle), 0.0,
            sin(angle), cos(angle), 0.0,
            0.0, 0.0, 1.0
        );
        position = (position - center) * (rotY) + center;        // displace
        normal = (normal) * (rotY);

        return position + position *max(0.0, start);//+ displaceNoise * max(0.0, start) * .2;
    }
    `;
    static fragmentShader = /* glsl */ `
    ${voronoiFnc}

   
    vec4 transition_crack_fragment(vec3 position, vec3 normal, float progress, float time) {
        ${sharedMain}
        
        // if(border == 1.0) discard;
        float cracks = 1.0-smoothstep(0.001, 0.002, c.x ) ;

        float fadeRange = 0.2;

        float cracksFade = 1.0-smoothstep( start, start + fadeRange, c.x ) ;
        vec3 col = vec3(cracks * crackProgress);
        col -=  -(vPositionCrack.z) * 1.3;
        
        float hue = c.x * 30.0 + .4 - u_time * .1;
        vec3 rainbowColor = hsv2rgb(vec3(fract(hue), 0.5, 1.0));
        col += rainbowColor * pow(cracksFade, 1.0) * crackProgress * .2;
        // col +=  pow(cracksFade, 5.0)*.25 * crackProgress;
        // return vec4(col, max(0.0, 1.0-border));
        float floorAlpha = 0.12;
        float borderAlpha = 1.0-smoothstep( floorAlpha, floorAlpha + 0.001, c.x ) ;
        // return vec4(vec3(c.z),1.0);
        float finalAlpha = 1.0 - border;//min(border, borderAlpha);
        if(finalAlpha == 0.0) discard;
        // return vec4(vec3(c.z),1.0 - borderAlpha);
        return vec4(col,finalAlpha);
    }
    `;
    static getPackAnimationProps = (progress) => {
        return {
            idle: Math.max(0.0, 1 - progress / 0.1),
            shakePower: Math.min(1, progress / 0.1) * 0.003,
            offset: new Vector3(0, 0, Math.pow(Math.min(1, progress * 3), 2) * -2),
            scale: 1 + Math.pow(progress, 3.0),
        };
    };
}

export default CrackTransition;
